import html2canvas from 'html2canvas-pro';
import { freezeBoardCaptures } from '../boards/runtime/capture';

export function captureViewport() {
  const boardCaptures = freezeBoardCaptures();
  const width = window.innerWidth, height = window.innerHeight;
  const sourceBase = document.baseURI;
  const scale = Math.min(window.devicePixelRatio || 1, 2, 8192 / width, 8192 / height, Math.sqrt(16_000_000 / (width * height)));
  // html2canvas clones synchronously before its first await. Call before showing the editor,
  // so a later socket revision cannot replace the DOM corresponding to the saved context.
  return html2canvas(document.documentElement, {
    width, height, x: window.scrollX, y: window.scrollY, scale, logging: false,
    windowWidth: width, windowHeight: height, scrollX: window.scrollX, scrollY: window.scrollY,
    backgroundColor: getComputedStyle(document.body).backgroundColor || '#f6f3e9',
    onclone: async doc => {
      for (const canvas of doc.querySelectorAll<HTMLCanvasElement>('canvas[data-board-capture]')) {
        const image = doc.createElement('img'), data = boardCaptures.get(canvas.dataset.boardCapture ?? '');
        if (!data) throw new Error('The board could not be captured.');
        image.src = data; image.className = canvas.className; image.style.cssText = canvas.style.cssText;
        image.width = canvas.width; image.height = canvas.height; image.setAttribute('aria-hidden', 'true'); canvas.replaceWith(image);
      }
      // The canvas renderer does not honor the browser's closed-details subtree.
      for (const details of doc.querySelectorAll('details:not([open])')) {
        for (const child of details.children) if (child.tagName !== 'SUMMARY') (child as HTMLElement).style.display = 'none';
      }
      for (const summary of doc.querySelectorAll<HTMLElement>('details > summary')) {
        summary.style.listStyleType = 'none';
        const marker = doc.createElement('span'); marker.textContent = summary.parentElement?.hasAttribute('open') ? '▾ ' : '▸ ';
        marker.style.marginRight = '4px'; summary.prepend(marker);
      }
      // Backdrop compositing can erase an entire HUD in reconstructed screenshots.
      // Preserve its content and surface while omitting only the background blur.
      for (const el of doc.querySelectorAll<HTMLElement>('.hud')) el.style.backdropFilter = 'none';
      try { await inlineSvgArtwork(doc, sourceBase); }
      catch (error) {
        // html2canvas's render cleanup starts after onclone; clean up a failed clone here.
        doc.defaultView?.frameElement?.remove();
        throw error;
      }
    },
    ignoreElements: el => el.hasAttribute('data-issue-private') ||
      el.matches('input[type="password"], input[name="token"], input[name="password"], [data-issue-ui]'),
  });
}

/** Standalone SVG images cannot fetch their external <image> resources. The screenshot
 * renderer serializes each SVG to a data URL, so embed its existing artwork first.
 * Work only on the frozen clone: never swap live card images or reveal deferred cards. */
async function inlineSvgArtwork(doc: Document, sourceBase: string) {
  const images = new Map<string, SVGImageElement[]>();
  for (const image of doc.querySelectorAll<SVGImageElement>('svg image')) {
    const href = image.getAttribute('href') ?? image.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
    if (!href || href.startsWith('data:')) continue;
    const url = new URL(href, sourceBase);
    if (url.origin !== new URL(sourceBase).origin || !['http:', 'https:'].includes(url.protocol)) throw new Error('Artwork cannot be captured.');
    const group = images.get(url.href) ?? []; group.push(image); images.set(url.href, group);
  }
  const entries = [...images];
  if (entries.length > 2048) throw new Error('Too much artwork to capture.');
  let next = 0, totalBytes = 0;
  await Promise.all(Array.from({ length: Math.min(4, entries.length) }, async () => {
    while (next < entries.length) {
      const [url, targets] = entries[next++]!;
      const response = await fetch(url, { credentials: 'omit', cache: 'force-cache', redirect: 'error', signal: AbortSignal.timeout(10000) });
      const type = response.headers.get('content-type')?.split(';')[0];
      if (!response.ok || !response.body || !type || !['image/png', 'image/jpeg', 'image/webp'].includes(type)) throw new Error('Artwork cannot be captured.');
      const reader = response.body.getReader(), chunks: Uint8Array<ArrayBuffer>[] = [];
      let bytes = 0;
      try {
        while (true) {
          const { done, value } = await reader.read(); if (done) break;
          bytes += value.byteLength; totalBytes += value.byteLength;
          if (bytes > 8 * 1024 * 1024 || totalBytes > 64 * 1024 * 1024) throw new Error('Too much artwork to capture.');
          chunks.push(value);
        }
      } finally { await reader.cancel(); reader.releaseLock(); }
      const data = await new Promise<string>((resolve, reject) => {
        const file = new FileReader(); file.onload = () => resolve(file.result as string); file.onerror = () => reject(new Error('Artwork cannot be captured.'));
        file.readAsDataURL(new Blob(chunks, { type }));
      });
      for (const target of targets) {
        target.setAttribute('href', data);
        target.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
      }
    }
  }));
}

export async function imageFromFile(file: File): Promise<HTMLCanvasElement> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 6 * 1024 * 1024) throw new Error('Choose a PNG, JPEG or WebP under 6 MB.');
  const bitmap = await createImageBitmap(file);
  try {
    if (bitmap.width > 8192 || bitmap.height > 8192 || bitmap.width * bitmap.height > 16_000_000) throw new Error('This image is too large. Use an image up to 16 megapixels.');
    const canvas = document.createElement('canvas'); canvas.width = bitmap.width; canvas.height = bitmap.height;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0); return canvas;
  } finally { bitmap.close(); }
}
